import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

/**
 * Upsert Warehouse Hierarchy
 *
 * Takes ONE hierarchy edge as single-value input sockets, shaped after the Gently
 * model (`im_warehouse_hierarchy`) — not after any source system. A CDC observer,
 * a curated mapping table, or a derivation step maps its own columns into these
 * sockets, so the same step serves every source.
 *
 * This is PASS TWO of warehouse ingest, and the ordering is a real constraint
 * rather than a detail: both endpoints are FKs to `im_warehouses`, so every
 * warehouse must exist before any edge can be written. Run `upsert_warehouse` to
 * completion first.
 *
 * Both codes resolve through ONE warehouse lookup, and misses are LOGGED per end
 * so an operator can tell whether the hub or the branch is missing. An edge from
 * a warehouse to itself is skipped: the DB would accept the self-loop and
 * hierarchy walks would not terminate. A malformed `activeFrom`/`activeTo` is
 * skipped too, since the write casts both to `date`.
 *
 * Per-row output is `written` / `skipped` only. Which of those checks dropped a
 * row is logged with the offending codes rather than emitted, because nothing
 * downstream of this leaf step branches on the reason.
 *
 * The conflict target is `(parent, child, activeFrom)` — the table's unique
 * index — so `activeFrom` is part of the KEY: a differing date INSERTS a second
 * edge instead of moving the first. Two consequences:
 *
 * - The supplied `activeFrom` is ignored when an open edge already exists for
 *   the same (parent, child, relationship type); that edge's stored period start
 *   is reused so a re-ingest updates in place. A mapping frame asserts current
 *   truth, not a period, and feeding its version date at rows stored under an
 *   earlier one produced 70 duplicate open edges.
 * - The step DOES close what it supersedes (`closeSupersededEdges`, default
 *   true), but only for relationship types where a child has at most one live
 *   parent (`primary_hub`). `sibling_branch` is many-to-many, mutual, and
 *   derived at read time since #5572, so stored sibling rows are counted and
 *   left untouched — a one-parent-per-child rule would close all but one of them.
 *
 * Superseded edges are chosen by `created_at`, never `active_from`: in the real
 * table the newer rows carry the OLDER date, so ordering by `active_from` closes
 * exactly the wrong ones.
 *
 * With a `batch` config the engine hands whole chunks to the bulk handler. A
 * leaf step: per-row outputs are execution results, not a downstream data
 * contract.
 */
export const upsertWarehouseHierarchyStep = defineStepConfig({
  type: "upsert_warehouse_hierarchy",
  name: "Upsert Warehouse Hierarchy",
  description:
    "Upserts one warehouse hierarchy edge (child code, parent code, relationship type, sourcing priority, cross-division flag, active dates) into im_warehouse_hierarchy, reusing an open edge's period start so a re-ingest cannot duplicate it, and closing the edges it supersedes for exclusive types. Unknown warehouses, self-edges, junk codes and malformed dates are skipped and reported, never created.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    childWarehouseCode: z
      .string()
      .describe("Origin code of the child warehouse (e.g. the branch)"),
    parentWarehouseCode: z
      .string()
      .describe("Origin code of the parent warehouse (e.g. the hub)"),
    relationshipType: z
      .string()
      .describe("Edge type, e.g. `PRIMARY_HUB` or `SIBLING_BRANCH`"),
    sourcingPriority: z
      .number()
      .int()
      .nullable()
      .optional()
      .describe(
        "Preference order for sourcing along this edge; lower sorts first. Defaults to 0.",
      ),
    crossDivisionAllowed: z
      .boolean()
      .nullable()
      .optional()
      .describe(
        "Whether this edge may be used across divisions. Defaults to false.",
      ),
    activeFrom: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Date the edge becomes active (YYYY-MM-DD). IGNORED when an open edge already exists for the same (parent, child, relationship type) — that edge's stored period start is reused, so a re-ingest updates in place instead of opening a duplicate period. Used as given when there is no open edge, which is what opens a new period after a closed one. Defaults to today (UTC).",
      ),
    activeTo: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Date the edge stops being active, or null for an open edge. Null REOPENS a previously closed edge.",
      ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The edge was upserted"),
    skipped: z
      .boolean()
      .describe(
        "The row was skipped — the reason (junk code, unresolved code, self-edge, malformed date, reversed activeFrom/activeTo window) is logged, not emitted",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    closeSupersededEdges: z
      .boolean()
      .prefault(true)
      .describe(
        "Stamp active_to on open edges this run supersedes (exclusive relationship types only, e.g. primary_hub). Turn off only to load a historical timeline deliberately — leaving it off is what leaves a corrected hub edge sitting beside the wrong one.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
